import { Head, usePage } from '@inertiajs/react'
import { useCallback, useState } from 'react'

import AboutYouInfoHint from '../../components/tools/AboutYouInfoHint'
import {
  buildAboutYouClientSnapshot,
  type AboutYouClientSnapshot,
  type VisitorInfoSection,
} from '../../lib/visitorClientSnapshot'
import type { SharedInertiaProps } from '../../types/inertiaShared'

type ServerSnapshot = {
  remote_ip: string
  user_agent: string
  accept_language: string
  forwarded_for: string
  true_client_ip: string
  cf_connecting_ip: string
  host: string
  referer: string
}

type PageProps = SharedInertiaProps & {
  server_snapshot: ServerSnapshot
}

function SectionBlock({ section }: { section: VisitorInfoSection }) {
  return (
    <section className="rounded-lg border border-outline-variant/20 bg-surface-container-low p-5">
      <h2 className="font-headline text-sm font-bold uppercase tracking-wide text-on-surface">{section.title}</h2>
      <dl className="mt-4 space-y-3">
        {section.rows.map((row, index) => (
          <div
            key={`${section.title}-${row.label}-${index}`}
            className="min-w-0 border-b border-outline-variant/10 pb-3 last:border-b-0 last:pb-0"
          >
            <dt className="flex items-start gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
              <span className="min-w-0 flex-1">{row.label}</span>
              <AboutYouInfoHint label={row.label} text={row.info} />
            </dt>
            <dd className="mt-1 whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-on-surface">
              {row.value || "(empty)"}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

export default function AboutYouTool() {
  const { server_snapshot: serverSnapshot } = usePage<PageProps>().props
  const [clientSnapshot, setClientSnapshot] = useState<AboutYouClientSnapshot>(() => buildAboutYouClientSnapshot())

  const refreshClient = useCallback(() => {
    setClientSnapshot(buildAboutYouClientSnapshot())
  }, [])

  const serverSection: VisitorInfoSection = {
    title: "This request (from the server)",
    rows: [
      {
        label: "IP as seen by the app (remote_ip)",
        value: serverSnapshot.remote_ip,
        info: "Address the site sees as the client after any trusted reverse proxies forward the real connection details. With no proxy it is usually the visitor; with proxies it may be the nearest hop unless headers are applied you trust.",
      },
      {
        label: "Forwarded For header",
        value: serverSnapshot.forwarded_for || "(not sent)",
        info: "A de facto header used by many proxies: a comma-separated list of addresses from client toward your site. Treat the leftmost as the original visitor only when you trust every hop that appended the list.",
      },
      {
        label: "True-Client-IP header",
        value: serverSnapshot.true_client_ip || "(not sent)",
        info: "Some networks add this name with the address they believe belongs to the person browsing. Naming and meaning vary by provider; not every site will send it.",
      },
      {
        label: "CF-Connecting-IP header",
        value: serverSnapshot.cf_connecting_ip || "(not sent)",
        info: "One of several provider-specific headers used when a proxy terminates the browser connection and the origin needs to recover the visitor address. Only present when that kind of network sits in front of the site.",
      },
      {
        label: "Host",
        value: serverSnapshot.host,
        info: "Host name and usually port the browser intended for this request. HTTP uses it so one machine can serve many sites on the same address.",
      },
      {
        label: "User-Agent header",
        value: serverSnapshot.user_agent || "(not sent)",
        info: "Self-reported product and version string from the user agent. It is a hint only and is easy to change, so it must not be used as proof of identity.",
      },
      {
        label: "Accept-Language header",
        value: serverSnapshot.accept_language || "(not sent)",
        info: "Weighted language preferences the browser attaches so the site can choose translations or regional content.",
      },
      {
        label: "Referer header",
        value: serverSnapshot.referer || "(not sent)",
        info: "When present, the address of the previous page that led here. Browsers and policies often trim or omit it for privacy.",
      },
    ],
  }

  const limitsSection: VisitorInfoSection = {
    title: "What normal web pages cannot read",
    rows: [
      {
        label: "LAN MAC address and similar hardware identifiers",
        value:
          "Websites cannot read your network interface MAC address or similar low-level identifiers. Browsers do not expose that to JavaScript.",
        info: "The web platform never hands hardware MAC or Bluetooth IDs to arbitrary sites. That boundary protects local network identity.",
      },
      {
        label: "Installed browser extensions",
        value:
          "There is no standard API to list extensions. Sites sometimes guess specific extensions from side effects, but you never get a full list.",
        info: "Extension interfaces are privileged. A normal page cannot list what is installed the way system settings can.",
      },
      {
        label: "Other origins and HttpOnly cookies",
        value:
          "Scripts on this page only see this origin. HttpOnly cookies and other sites' data stay isolated and hidden from script where the platform enforces it.",
        info: "Same-origin policy and HttpOnly keep most cookies and storage off limits to scripts. Another tab's data stays in that origin's boundary.",
      },
    ],
  }

  return (
    <>
      <Head title="About you" />
      <div className="space-y-8">
        <header className="border-b border-outline-variant/20 pb-6">
          <h1 className="font-headline text-3xl font-bold tracking-tighter text-on-surface uppercase">About you</h1>
          <p className="mt-2 max-w-3xl font-mono text-xs leading-relaxed text-on-surface-variant">
            What traveled with this page load over HTTP, and a short list of values scripts on this site can read from the
            browser here. Use it to think about the boundary between the page, the network, and your device.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => refreshClient()}
              className="inline-flex items-center gap-2 rounded-lg border border-outline-variant/35 bg-surface-container-high px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-on-surface transition-colors hover:border-primary-container hover:text-primary-container"
            >
              <span className="material-symbols-outlined text-[18px]" aria-hidden>
                refresh
              </span>
              Refresh client data
            </button>
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-2">
          <SectionBlock section={serverSection} />
          <SectionBlock section={limitsSection} />
        </div>

        <div>
          <h2 className="mb-4 font-headline text-lg font-bold uppercase tracking-tight text-on-surface">
            In your browser (this tab)
          </h2>
          <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
            <SectionBlock section={clientSnapshot.navigator} />
            <div className="flex min-w-0 flex-col gap-4">
              <SectionBlock section={clientSnapshot.viewportAndScreen} />
              <SectionBlock section={clientSnapshot.localeAndTime} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
